import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages the gift wrap flyout panel.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLDivElement} messageSection – The gift message section.
 * @property {HTMLInputElement} recipientInput – The recipient name input.
 * @property {HTMLInputElement} senderInput – The sender name input.
 * @property {HTMLTextAreaElement} messageInput – The gift message textarea.
 * @property {HTMLButtonElement} addToCartButton – The add to cart button.
 * @property {HTMLSpanElement} addToCartText – The add to cart button text.
 * @property {HTMLSpanElement} loadingSpinner – The loading spinner.
 * @property {HTMLDivElement} mixedItemsWarning – The warning for mixed items.
 * @property {HTMLSpanElement} charCount – The character count display.
 *
 * @extends DialogComponent
 */
class GiftWrapFlyout extends DialogComponent {
  /** @type {RegExp} Allowed characters regex */
  static ALLOWED_CHARS = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/? \s]*$/;

  /** @type {string} sessionStorage key for pending auto-removal */
  static PENDING_REMOVAL_KEY = 'giftWrapRemovePending';

  /** @type {string | null} The line item key being edited */
  #editLineItemKey = null;

  /** @type {string | null} The variant ID being edited */
  #editVariantId = null;

  /** @type {boolean} */
  #removeInFlight = false;

  get #isEditMode() {
    return this.#editLineItemKey !== null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setupListeners();
    this.#checkCartForGiftWrap(true);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateMessageSectionVisibility();
  }

  #setupListeners() {
    // Input validation
    const inputs = [this.refs.recipientInput, this.refs.senderInput, this.refs.messageInput];
    inputs.forEach((input) => {
      if (!input) return;
      input.addEventListener('input', () => this.#sanitizeInput(input));
      input.addEventListener('paste', () => setTimeout(() => this.#sanitizeInput(input), 0));
    });

    // Character count for message textarea
    const { messageInput } = this.refs;
    if (messageInput) {
      messageInput.addEventListener('input', () => this.#updateCharCount());
    }

    // Product selection - use event delegation
    this.addEventListener('change', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      if (target.name === 'gift_wrap_product') {
        this.#onProductSelectionChange();
      }
    });

    // Cart updates
    document.addEventListener('cart:update', this.#onCartUpdate);
    document.addEventListener('cart:change', this.#onCartUpdate);

    // Edit events
    window.addEventListener('gift-wrap:edit', this.#onEditEvent);

  }

  #onEditEvent = (/** @type {Event} */ event) => {
    this.openFlyoutForEdit(/** @type {CustomEvent} */ (event).detail);
  };

  #onCartUpdate = () => {
    setTimeout(() => this.#checkCartForGiftWrap(false), 100);
  };

  /**
   * Sanitizes input by removing invalid characters.
   * @param {HTMLInputElement | HTMLTextAreaElement} input
   */
  #sanitizeInput(input) {
    const regex = GiftWrapFlyout.ALLOWED_CHARS;
    if (!regex.test(input.value)) {
      input.value = input.value.split('').filter((c) => regex.test(c)).join('');
    }
  }

  #onProductSelectionChange() {
    const hasSelection = this.querySelector('input[name="gift_wrap_product"]:checked') !== null;
    const { addToCartButton } = this.refs;

    if (addToCartButton) addToCartButton.disabled = !hasSelection;
    this.#updateMessageSectionVisibility();
  }

  /**
   * Update message section visibility. Visible only when a gift wrap product is selected.
   */
  #updateMessageSectionVisibility() {
    const hasSelection = this.querySelector('input[name="gift_wrap_product"]:checked') !== null;
    const { messageSection } = this.refs;

    if (messageSection) {
      messageSection.hidden = !hasSelection;
    }
  }

  async #checkCartForGiftWrap(/** @type {boolean} */ isInitialCheck) {
    const giftWrapEditButton = document.querySelector('.cart-items__edit-gift-wrap');
    const hasGiftableItems = this.dataset.hasGiftableItems === 'true';
    const hasPendingRemoval = this.#hasPendingRemoval();
    const pendingLineItemKey = this.#getPendingRemovalLineItemKey();
    const giftWrapLineItemKey = giftWrapEditButton?.dataset.lineItemKey || pendingLineItemKey;

    // Clear cart attributes if gift wrap was removed (manually or auto)
    if (!giftWrapEditButton && !giftWrapLineItemKey) {
      if (hasPendingRemoval) this.#clearPendingRemoval();
      await this.#clearCartAttributes();
    }

    this.classList.toggle('cart-gift-wrap--hidden', !hasGiftableItems);
    this.#updateWarningVisibility();

    // Auto-remove gift wrap if no giftable items remain
    if (hasGiftableItems && hasPendingRemoval) {
      this.#clearPendingRemoval();
    }

    if (!hasGiftableItems && giftWrapLineItemKey) {
      await this.#removeGiftWrapItem(giftWrapLineItemKey, {
        suppressDrawerOpen: isInitialCheck && hasPendingRemoval,
      });
    }
  }

  async #removeGiftWrapItem(
    /** @type {string} */ lineItemKey,
    /** @type {{ suppressDrawerOpen?: boolean }} */ options = {}
  ) {
    if (this.#removeInFlight) return;
    this.#removeInFlight = true;
    this.#setPendingRemoval(lineItemKey);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0,
          sections: this.#getSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Failed to remove gift wrap');

      // Clear cart attributes when gift wrap is removed
      await this.#clearCartAttributes();

      const data = await response.json();
      this.#dispatchCartEvent(data, 'gift-wrap-auto-remove', {
        source: 'gift-wrap-auto-remove',
        suppressDrawerOpen: options.suppressDrawerOpen,
      });
      this.#clearPendingRemoval();
    } catch (error) {
      console.error('Error removing gift wrap:', error);
      if (!document.querySelector('.cart-items__edit-gift-wrap')) {
        this.#clearPendingRemoval();
      }
    } finally {
      this.#removeInFlight = false;
    }
  }

  #hasPendingRemoval() {
    return Boolean(this.#getPendingRemovalData());
  }

  #getPendingRemovalLineItemKey() {
    const data = this.#getPendingRemovalData();
    return data?.lineItemKey || null;
  }

  #getPendingRemovalData() {
    try {
      const rawValue = sessionStorage.getItem(GiftWrapFlyout.PENDING_REMOVAL_KEY);
      if (!rawValue) return null;
      if (rawValue === 'true') return { pending: true };
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object') return parsed;
      return { pending: true };
    } catch (error) {
      return null;
    }
  }

  #setPendingRemoval(/** @type {string | null} */ lineItemKey = null) {
    try {
      if (lineItemKey) {
        sessionStorage.setItem(
          GiftWrapFlyout.PENDING_REMOVAL_KEY,
          JSON.stringify({ lineItemKey })
        );
      } else {
        sessionStorage.setItem(GiftWrapFlyout.PENDING_REMOVAL_KEY, 'true');
      }
    } catch (error) {
      // ignore storage errors
    }
  }

  #clearPendingRemoval() {
    try {
      sessionStorage.removeItem(GiftWrapFlyout.PENDING_REMOVAL_KEY);
    } catch (error) {
      // ignore storage errors
    }
  }

  /**
   * Updates cart attributes with gift message.
   * @param {Record<string, string>} properties
   */
  async #updateCartAttributes(properties) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            giftMessage: properties.Message || '',
          },
        }),
      });
    } catch (error) {
      console.error('Error updating cart attributes:', error);
    }
  }

  /**
   * Clears gift-related cart attributes.
   */
  async #clearCartAttributes() {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            giftMessage: '',
          },
        }),
      });
    } catch (error) {
      console.error('Error clearing cart attributes:', error);
    }
  }

  /**
   * Opens the flyout dialog.
   */
  openFlyout() {
    this.#resetState();
    this.#prepareDialog(false);
    this.showDialog();
    setTimeout(() => this.#updateMessageSectionVisibility(), 0);
  }

  /**
   * Opens the flyout in edit mode.
   * @param {object} data
   * @param {string} data.lineItemKey
   * @param {string} data.variantId
   * @param {string} [data.giftTo]
   * @param {string} [data.giftFrom]
   * @param {string} [data.giftMessage]
   */
  openFlyoutForEdit(data) {
    this.#editLineItemKey = data.lineItemKey;
    this.#editVariantId = data.variantId;

    // Pre-fill form
    const { recipientInput, senderInput, messageInput } = this.refs;
    if (recipientInput) recipientInput.value = data.giftTo || '';
    if (senderInput) senderInput.value = data.giftFrom || '';
    if (messageInput) messageInput.value = data.giftMessage || '';

    // Update character count for pre-filled message
    this.#updateCharCount();

    // Select product
    const radio = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input[name="gift_wrap_product"][value="${data.variantId}"]`)
    );
    if (radio) radio.checked = true;

    this.#prepareDialog(true);
    this.showDialog();
    setTimeout(() => this.#updateMessageSectionVisibility(), 0);
  }

  #resetState() {
    this.#editLineItemKey = null;
    this.#editVariantId = null;
    this.#clearForm();
    this.#resetProductSelection();
  }

  #prepareDialog(/** @type {boolean} */ isEdit) {
    this.#updateButtonText();
    this.#updateWarningVisibility();

    const { messageSection, addToCartButton } = this.refs;
    const hasSelection = this.querySelector('input[name="gift_wrap_product"]:checked') !== null;

    if (messageSection) {
      messageSection.hidden = !hasSelection;
    }
    if (addToCartButton) addToCartButton.disabled = !isEdit;
  }

  #updateButtonText() {
    const { addToCartText, addToCartTextEdit } = this.refs;
    if (addToCartText) {
      addToCartText.textContent = this.#isEditMode ? addToCartTextEdit.innerText : addToCartText.innerText;
    }
  }

  #updateWarningVisibility() {
    const warning = /** @type {HTMLElement | null} */ (this.refs.mixedItemsWarning);
    if (warning) {
      const hasUngiftableItems = warning.dataset.hasUngiftableItems === 'true';
      warning.classList.toggle('cart-gift-wrap__warning--hidden', !hasUngiftableItems);
    }
  }

  #resetProductSelection() {
    this.querySelectorAll('input[name="gift_wrap_product"]').forEach((radio) => {
      /** @type {HTMLInputElement} */ (radio).checked = false;
    });
  }

  #clearForm() {
    const { recipientInput, senderInput, messageInput } = this.refs;
    if (recipientInput) recipientInput.value = '';
    if (senderInput) senderInput.value = '';
    if (messageInput) messageInput.value = '';
    this.#updateCharCount();
  }

  #updateCharCount() {
    const { messageInput, charCount } = this.refs;
    if (!messageInput || !charCount) return;
    const currentLength = messageInput.value.length;
    const maxLength = messageInput.maxLength || 200;
    charCount.textContent = `${currentLength}/${maxLength}`;
  }

  /**
   * Adds or updates the gift wrap product.
   */
  async addToCart() {
    const selectedProduct = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="gift_wrap_product"]:checked')
    );
    if (!selectedProduct) return;

    const variantId = selectedProduct.value;
    const properties = this.#getFormProperties();

    this.#setLoading(true);

    try {
      if (this.#isEditMode) {
        await this.#handleUpdate(variantId, properties);
      } else {
        await this.#handleAdd(variantId, properties);
      }

      this.#clearForm();
      this.closeFlyout();
    } catch (error) {
      console.error('Error with gift wrap:', error);
    } finally {
      this.#setLoading(false);
    }
  }

  #getFormProperties() {
    const { recipientInput, senderInput, messageInput } = this.refs;
    const recipientName = recipientInput?.value?.trim() || '';
    const senderName = senderInput?.value?.trim() || '';
    const giftMessage = messageInput?.value?.trim() || '';

    // In edit mode, always include all fields to allow clearing
    if (this.#isEditMode) {
      return { To: recipientName, From: senderName, Message: giftMessage };
    }

    // In add mode, only include non-empty fields
    /** @type {Record<string, string>} */
    const props = {};
    if (recipientName) props['To'] = recipientName;
    if (senderName) props['From'] = senderName;
    if (giftMessage) props['Message'] = giftMessage;
    return props;
  }

  async #handleAdd(/** @type {string} */ variantId, /** @type {Record<string, string>} */ properties) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: Object.keys(properties).length ? properties : undefined }],
        sections: this.#getSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) throw new Error('Failed to add to cart');

    const data = await response.json();

    // Also save to cart attributes
    await this.#updateCartAttributes(properties);

    this.#dispatchCartEvent(data, variantId);
  }

  async #handleUpdate(/** @type {string} */ variantId, /** @type {Record<string, string>} */ properties) {
    const productChanged = variantId !== this.#editVariantId;

    if (productChanged) {
      // Remove old, add new
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.#editLineItemKey, quantity: 0 }),
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties }],
          sections: this.#getSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Failed to update cart');
      this.#dispatchCartEvent(await response.json(), variantId);
    } else {
      // Just update properties
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: this.#editLineItemKey,
          quantity: 1,
          properties,
          sections: this.#getSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Failed to update cart');
      this.#dispatchCartEvent(await response.json(), variantId);
    }

    // Also update cart attributes
    await this.#updateCartAttributes(properties);

    this.#editLineItemKey = null;
    this.#editVariantId = null;
  }

  /**
   * @param {{ item_count?: number, sections?: Record<string, string> }} responseData
   * @param {string} sourceId
   * @param {{ source?: string, suppressDrawerOpen?: boolean }} [extraData]
   */
  #dispatchCartEvent(responseData, sourceId, extraData = {}) {
    document.dispatchEvent(
      new CartAddEvent({}, sourceId, {
        source: extraData.source || 'gift-wrap-flyout',
        itemCount: responseData.item_count ?? 1,
        sections: responseData.sections,
        ...extraData,
      })
    );
  }

  #getSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
      if (id) ids.push(id);
    });
    return ids;
  }

  #setLoading(/** @type {boolean} */ isLoading) {
    const { addToCartButton, addToCartText, loadingSpinner } = this.refs;
    if (addToCartButton) addToCartButton.disabled = isLoading;
    if (addToCartText) addToCartText.hidden = isLoading;
    if (loadingSpinner) loadingSpinner.hidden = !isLoading;
  }

  /**
   * Closes the flyout and resets state.
   */
  closeFlyout = async () => {
    this.#editLineItemKey = null;
    this.#editVariantId = null;
    await this.closeDialog();
  };
}

if (!customElements.get('gift-wrap-flyout')) {
  customElements.define('gift-wrap-flyout', GiftWrapFlyout);
}
